import { Check, X, TrendingDown, Trophy, MessageSquare, Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Notification } from "./NotificationsPanel";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onClick: (notification: Notification) => void;
}

export const NotificationItem = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onClick,
}: NotificationItemProps) => {
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "new_feedback":
        return <MessageSquare className="h-3.5 w-3.5 text-info" />;
      case "sentiment_alert":
        return <TrendingDown className="h-3.5 w-3.5 text-destructive" />;
      case "milestone":
        return <Trophy className="h-3.5 w-3.5 text-warning" />;
      default:
        return <Bell className="h-3.5 w-3.5" />;
    }
  };

  const formatTime = (timestamp: string) => {
    const distance = formatDistanceToNow(new Date(timestamp), { addSuffix: false });
    return distance
      .replace("about ", "")
      .replace(" minutes", "m")
      .replace(" minute", "m")
      .replace(" hours", "h")
      .replace(" hour", "h")
      .replace(" days", "d")
      .replace(" day", "d")
      .replace("less than a minute", "now");
  };

  return (
    <div
      className={`p-3 hover:bg-muted/50 transition-colors cursor-pointer ${
        !notification.read ? "bg-muted/30" : ""
      }`}
      onClick={() => onClick(notification)}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">{getNotificationIcon(notification.type)}</div>
        <div className="flex-1 min-w-0">
          {/* Title row with inline page badge */}
          <div className="flex items-center gap-1.5 text-sm">
            <span className="font-medium truncate">{notification.title}</span>
            {notification.pageTitle && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground truncate">{notification.pageTitle}</span>
              </>
            )}
          </div>
          
          {/* Single line message */}
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
            {notification.message}
          </p>
          
          {/* Timestamp and actions row */}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] text-muted-foreground">
              {formatTime(notification.timestamp)}
            </span>
            <div className="flex items-center gap-0.5">
              {!notification.read && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkAsRead(notification.id);
                  }}
                  className="h-6 w-6 p-0 hover:bg-accent"
                  aria-label="Mark as read"
                >
                  <Check className="h-3 w-3" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(notification.id);
                }}
                className="h-6 w-6 p-0 hover:bg-accent"
                aria-label="Dismiss notification"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
